import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { FirstTaskSuggestion, MemoryHighlightItem } from './wizardApi';
import { ReactComponent as WhatsappColorIcon } from '../../icons/whatsapp-color.svg';

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const TASK_ICONS: Record<string, React.ReactNode> = {
  'book-restaurant': (
    <svg {...iconProps}>
      <path d='M6 3v7a2.2 2.2 0 004.4 0V3M8.2 10v11' />
      <path d='M17 3c-1.6 1-2.4 3-2.4 5.2 0 1.6.8 2.6 2.4 2.8V21' />
    </svg>
  ),
  'book-haircut': (
    <svg {...iconProps}>
      <circle cx='6' cy='18' r='2.8' />
      <circle cx='18' cy='18' r='2.8' />
      <path d='M19.5 4L8.2 16.2M4.5 4l11.3 12.2' />
    </svg>
  ),
  'book-gym-class': (
    <svg {...iconProps}>
      <path d='M6.5 8v8M17.5 8v8M3.5 10v4M20.5 10v4M6.5 12h11' />
    </svg>
  ),
  'book-coworking-desk': (
    <svg {...iconProps}>
      <path d='M4 21V6.2a1.7 1.7 0 011.7-1.7h7.1a1.7 1.7 0 011.7 1.7V21' />
      <path d='M14.5 10.5h3.8A1.7 1.7 0 0120 12.2V21M2.5 21h19' />
      <path d='M7.3 8.4h1M11.2 8.4h1M7.3 12.3h1M11.2 12.3h1M7.3 16.2h1M11.2 16.2h1' />
    </svg>
  ),
  'find-flights': (
    <svg {...iconProps}>
      <path d='M12 2.5c.9 0 1.6.9 1.6 2v4.6l7.4 4.3v2.3l-7.4-2.2v3.9l2.6 1.9v1.9L12 20l-4.2 1.2v-1.9l2.6-1.9v-3.9L3 15.7v-2.3l7.4-4.3V4.5c0-1.1.7-2 1.6-2z' />
    </svg>
  ),
  'rebook-favourite': (
    <svg {...iconProps}>
      <path d='M20.5 12a8.5 8.5 0 11-2.5-6' />
      <path d='M18.5 2.5v3.7h-3.7' />
    </svg>
  ),
  'contact-known-pro': (
    <svg {...iconProps}>
      <path d='M14.5 6.5a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z' />
      <path d='M4 20.5c.7-3.6 3.5-5.5 7-5.5s6.3 1.9 7 5.5' />
    </svg>
  ),
  'repeat-trip': (
    <svg {...iconProps}>
      <path d='M12 21s-6.5-5.4-6.5-10a6.5 6.5 0 0113 0c0 4.6-6.5 10-6.5 10z' />
      <circle cx='12' cy='11' r='2.4' />
    </svg>
  ),
  'book-vet-groomer': (
    <svg {...iconProps}>
      <circle cx='7' cy='8.5' r='1.7' />
      <circle cx='12' cy='6.5' r='1.7' />
      <circle cx='17' cy='8.5' r='1.7' />
      <path d='M12 11.5c-2.6 0-5 2.2-5 4.6 0 1.5 1.1 2.4 2.5 2.4 1 0 1.7-.5 2.5-.5s1.5.5 2.5.5c1.4 0 2.5-.9 2.5-2.4 0-2.4-2.4-4.6-5-4.6z' />
    </svg>
  ),
  'rebook-activity': (
    <svg {...iconProps}>
      <circle cx='12' cy='12' r='8.75' />
      <path d='M12 3.25c2.4 2.2 3.7 5.3 3.7 8.75s-1.3 6.55-3.7 8.75M12 3.25c-2.4 2.2-3.7 5.3-3.7 8.75s1.3 6.55 3.7 8.75M3.6 9.5h16.8M3.6 14.5h16.8' />
    </svg>
  ),
  'book-table-trip': (
    <svg {...iconProps}>
      <path d='M3.5 10.5h17M5 10.5V19M19 10.5V19M8.5 14h7' />
      <path d='M12 3.5v7M9 5.5c0 1.4 1.3 2.5 3 2.5s3-1.1 3-2.5' />
    </svg>
  ),
  'calendar-recurring': (
    <svg {...iconProps}>
      <rect x='3.5' y='5' width='17' height='15.5' rx='2' />
      <path d='M3.5 9.5h17M8 2.8v4M16 2.8v4' />
      <path d='M14.8 13a3.2 3.2 0 10.9 2.9' />
      <path d='M15.9 12.2v2h-2' />
    </svg>
  ),
  'block-time': (
    <svg {...iconProps}>
      <rect x='3.5' y='5' width='17' height='15.5' rx='2' />
      <path d='M3.5 9.5h17M8 2.8v4M16 2.8v4' />
      <path d='M8 14.5h8' />
    </svg>
  ),
  'subscription-audit': (
    <svg {...iconProps}>
      <rect x='3' y='6' width='18' height='12.5' rx='2' />
      <path d='M3 10h18' />
      <path d='M7 14.8h4' />
    </svg>
  ),
  'find-gift': (
    <svg {...iconProps}>
      <rect x='3.5' y='8' width='17' height='4' rx='1' />
      <path d='M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7M12 8v13' />
      <path d='M12 8c-2.6 0-4.6-1-4.6-2.6C7.4 4 8.5 3 9.8 3c1.9 0 2.2 2.6 2.2 5zm0 0c2.6 0 4.6-1 4.6-2.6C16.6 4 15.5 3 14.2 3 12.3 3 12 5.6 12 8z' />
    </svg>
  ),
  'tidy-inbox': (
    <svg {...iconProps}>
      <path d='M3 13.5V7a2.5 2.5 0 012.5-2.5h13A2.5 2.5 0 0121 7v6.5' />
      <path d='M3 13.5h5.2l1.3 2.3h5l1.3-2.3H21V17a2.5 2.5 0 01-2.5 2.5h-13A2.5 2.5 0 013 17v-3.5z' />
    </svg>
  ),
  'brief-today': (
    <svg {...iconProps}>
      <circle cx='12' cy='12' r='8.75' />
      <path d='M12 7.2V12l3.4 2.1' />
    </svg>
  ),
  'research-doc': (
    <svg {...iconProps}>
      <path d='M6 2.8h8.3L19.2 7.7V21.2H6z' />
      <path d='M14 3v5h5M9 12h6M9 15.5h6M9 8.8h2' />
    </svg>
  ),
  'org-chart-doc': (
    <svg {...iconProps}>
      <rect x='9' y='3' width='6' height='4.5' rx='1' />
      <rect x='3' y='16.5' width='6' height='4.5' rx='1' />
      <rect x='15' y='16.5' width='6' height='4.5' rx='1' />
      <path d='M12 7.5v4M6 16.5v-2.5h12v2.5' />
    </svg>
  ),
};

const SOMETHING_ELSE_TASK: FirstTaskSuggestion = {
  id: 'something-else',
  label: 'Something else',
  tagline: 'You can ask for anything',
  evidence: null,
};

// The default (non-researched) suggestions and the "something else" card are
// fixed chrome, so their labels come from the locale files; researched
// suggestions keep the backend-generated label/tagline untouched.
const FIXED_TASK_LABEL_KEYS: Record<string, string> = {
  'book-restaurant': 'wizard.tasks.bookRestaurant',
  'book-haircut': 'wizard.tasks.bookHaircut',
  'find-flights': 'wizard.tasks.findFlights',
  'something-else': 'wizard.tasks.somethingElse',
};

const somethingElseIcon = (
  <svg {...iconProps}>
    <path d='M12 3.2a8.8 8.8 0 00-7.6 13.2L3.2 20.8l4.4-1.2A8.8 8.8 0 1012 3.2z' />
    <path d='M8.3 12h.01M12 12h.01M15.7 12h.01' />
  </svg>
);

const fallbackIcon = (
  <svg {...iconProps}>
    <circle cx='12' cy='12' r='8.75' />
    <path d='M9 12l2.2 2.2L15.4 9.8' />
  </svg>
);

/** Shown while research is running/failed (mirrors the backend defaults). */
export const DEFAULT_SUGGESTIONS: FirstTaskSuggestion[] = [
  {
    id: 'book-restaurant',
    label: 'Book a restaurant',
    tagline: null,
    evidence: null,
  },
  {
    id: 'book-haircut',
    label: 'Book a haircut',
    tagline: null,
    evidence: null,
  },
  { id: 'find-flights', label: 'Find flights', tagline: null, evidence: null },
];

export function taskLabel(
  task: string,
  suggestions: FirstTaskSuggestion[],
  t: TFunction,
): string {
  const suggested = suggestions.find((s) => s.id === task);
  if (suggested?.label && !FIXED_TASK_LABEL_KEYS[task]) {
    return suggested.label;
  }
  if (FIXED_TASK_LABEL_KEYS[task]) {
    return t(FIXED_TASK_LABEL_KEYS[task]);
  }
  return suggested?.label || t('wizard.tasks.yourFirstTask');
}

/** Delay between memory reveals, mirroring the prototype's cadence. */
const MEMORY_REVEAL_MS = 850;
/** Pause after the suggestions land before swapping the loader out. */
const LOADER_SWAP_MS = 700;
/**
 * Once the tasks are ready the loader hands over as soon as a few
 * highlights have been read or the screen has been up long enough,
 * even if the memory scan is still finding more.
 */
const LOADER_MAX_SCREEN_MS = 20000;
const LOADER_ENOUGH_HIGHLIGHTS = 5;
/**
 * Absolute ceiling on the discovery screen. Even if the task research has
 * not reported back, the page moves on to the default cards rather than
 * leaving the user stranded; researched cards swap in when they land.
 */
const LOADER_HARD_EXIT_MS = 40000;

/** Skeleton widths for the discovery rows, matching the prototype. */
const SKELETON_WIDTHS = [82, 74, 78, 58, 66, 62];

/**
 * Activity lines cycled while the scan is still working, so someone who
 * reaches this screen before the first real highlight lands still sees
 * the assistant visibly at work rather than bare shimmer rows.
 */
const SPLINE_PHRASE_KEYS = [
  'wizard.tasks.spline1',
  'wizard.tasks.spline2',
  'wizard.tasks.spline3',
  'wizard.tasks.spline4',
  'wizard.tasks.spline5',
  'wizard.tasks.spline6',
  'wizard.tasks.spline7',
  'wizard.tasks.spline8',
];
const SPLINE_CYCLE_MS = 2600;

const sparkIcon = (
  <svg
    viewBox='0 0 16 16'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M8 2.2l1.15 3.05L12.2 6.4 9.15 7.55 8 10.6 6.85 7.55 3.8 6.4l3.05-1.15z' />
    <path d='M12.4 10.6l.5 1.3 1.3.5-1.3.5-.5 1.3-.5-1.3-1.3-.5 1.3-.5z' />
  </svg>
);

/**
 * Discovery phase that owns the whole page while suggestions generate.
 * Each memory arrives on its own and stays so it can be read; once the
 * researched suggestions land it hands over after a few highlights have
 * been shown or the screen has been up long enough, whichever comes first.
 * `active` should be true only while the first-tasks screen is showing,
 * so the reveal cadence starts when the user actually lands on it.
 * `memoriesStatus`: 'pending' means accounts are connected but the scan has
 * not reported yet, so the loader waits rather than skipping.
 */
export type MemoryLoaderStatus =
  | 'none'
  | 'pending'
  | 'running'
  | 'ready'
  | 'failed';

export function useMemoryLoader(
  memories: MemoryHighlightItem[],
  tasksReady: boolean,
  active: boolean,
  memoriesStatus: MemoryLoaderStatus = 'none',
): { showLoader: boolean; revealed: MemoryHighlightItem[] } {
  const [revealCount, setRevealCount] = useState(0);
  const [swapped, setSwapped] = useState(false);
  const [started, setStarted] = useState(false);
  const [graceOver, setGraceOver] = useState(false);
  const queued = memories.length - revealCount;
  const scanPending =
    memoriesStatus === 'running' || memoriesStatus === 'pending';

  useEffect(() => {
    if (!active || started) return;
    setStarted(true);
    // Skip the loader only when the scan is genuinely over with nothing to
    // show; a scan that has not reported yet still deserves the wait.
    if (tasksReady && memories.length === 0 && !scanPending) setSwapped(true);
  }, [active, started, tasksReady, memories.length, scanPending]);

  const [timeUp, setTimeUp] = useState(false);
  useEffect(() => {
    if (!started || timeUp) return;
    const timer = window.setTimeout(
      () => setTimeUp(true),
      LOADER_MAX_SCREEN_MS,
    );
    return () => window.clearTimeout(timer);
  }, [started, timeUp]);

  const [hardTimeUp, setHardTimeUp] = useState(false);
  useEffect(() => {
    if (!started || hardTimeUp) return;
    const timer = window.setTimeout(
      () => setHardTimeUp(true),
      LOADER_HARD_EXIT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [started, hardTimeUp]);

  // Hold the loader past tasksReady only until a few highlights have been
  // read or the screen has been up long enough; a finished scan ends the
  // hold immediately.
  useEffect(() => {
    if (!started || !tasksReady || graceOver) return;
    if (!scanPending || revealCount >= LOADER_ENOUGH_HIGHLIGHTS || timeUp) {
      setGraceOver(true);
    }
  }, [started, tasksReady, graceOver, scanPending, revealCount, timeUp]);

  useEffect(() => {
    if (!started || swapped || queued <= 0) return;
    const timer = window.setTimeout(
      () => setRevealCount((n) => n + 1),
      revealCount === 0 ? 350 : MEMORY_REVEAL_MS,
    );
    return () => window.clearTimeout(timer);
  }, [started, swapped, queued, revealCount]);

  // Hand over once ready — queued highlights that never made it on screen
  // are dropped rather than holding the user here. The hard ceiling exits
  // unconditionally so slow research can never strand the user on this page.
  useEffect(() => {
    if (!started || swapped) return;
    if (!hardTimeUp) {
      if (!tasksReady || !graceOver) return;
      if (queued > 0 && !timeUp && revealCount < LOADER_ENOUGH_HIGHLIGHTS) {
        return;
      }
    }
    const timer = window.setTimeout(() => setSwapped(true), LOADER_SWAP_MS);
    return () => window.clearTimeout(timer);
  }, [
    started,
    tasksReady,
    graceOver,
    swapped,
    queued,
    timeUp,
    revealCount,
    hardTimeUp,
  ]);

  return {
    showLoader: !swapped && (started || !tasksReady),
    revealed: memories.slice(0, revealCount),
  };
}

interface TasksStepProps {
  eyebrow: string;
  assistantName: string;
  suggestions: FirstTaskSuggestion[];
  selectedTask: string | null;
  showValidation: boolean;
  showLoader: boolean;
  revealed: MemoryHighlightItem[];
  onSelect: (task: string) => void;
}

const TasksStep: React.FC<TasksStepProps> = ({
  eyebrow,
  assistantName,
  suggestions,
  selectedTask,
  showValidation,
  showLoader,
  revealed,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [splineIdx, setSplineIdx] = useState(0);
  useEffect(() => {
    if (!showLoader) return;
    const timer = window.setInterval(
      () => setSplineIdx((n) => (n + 1) % SPLINE_PHRASE_KEYS.length),
      SPLINE_CYCLE_MS,
    );
    return () => window.clearInterval(timer);
  }, [showLoader]);

  // Discovery owns the whole page while the suggestions are built: every
  // row is a shimmer placeholder from the start and swaps to a real memory
  // when found, so the page never reflows as they arrive.
  if (showLoader) {
    const rowCount = Math.max(SKELETON_WIDTHS.length, revealed.length);
    return (
      <section className='screen active' data-screen='tasks'>
        <div className='eyebrow'>{eyebrow}</div>
        <h1 className='ob-title'>{t('wizard.tasks.loaderTitle')}</h1>
        <p className='ob-lede'>{t('wizard.tasks.loaderLede')}</p>
        {revealed.length === 0 && (
          <div className='mem-spline' aria-hidden='true'>
            <span className='mem-spline-spinner' />
            <span key={splineIdx} className='mem-spline-text'>
              {t(SPLINE_PHRASE_KEYS[splineIdx])}&hellip;
            </span>
          </div>
        )}
        <div className='mem-wrap'>
          <ul className='mem-list' aria-live='polite'>
            {Array.from({ length: rowCount }, (_, i) => {
              const mem = revealed[i];
              return (
                <li key={i} className={`mem${mem ? ' in' : ''}`}>
                  {mem ? (
                    <>
                      <span className='mem-icon'>{sparkIcon}</span>
                      <span className='mem-text'>{mem.text}</span>
                    </>
                  ) : (
                    <span
                      className='mem-skel'
                      style={{
                        width: `${SKELETON_WIDTHS[i % SKELETON_WIDTHS.length]}%`,
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    );
  }

  return (
    <section className='screen active' data-screen='tasks'>
      <div className='eyebrow'>{eyebrow}</div>
      <h1 className='ob-title'>
        {t('wizard.tasks.title', { name: assistantName })}
      </h1>
      <p className='ob-lede'>{t('wizard.tasks.lede')}</p>

      <div className='group-cap'>{t('wizard.tasks.pickOne')}</div>

      <div
        className='task-list'
        role='radiogroup'
        aria-label={t('wizard.tasks.chooseAria')}
      >
        {[...suggestions, SOMETHING_ELSE_TASK].map((task) => (
          <button
            key={task.id}
            className='task'
            role='radio'
            aria-checked={selectedTask === task.id}
            onClick={() => onSelect(task.id)}
          >
            <span className='task-icon'>
              {task.id === SOMETHING_ELSE_TASK.id
                ? somethingElseIcon
                : TASK_ICONS[task.id] || fallbackIcon}
            </span>
            <span className='task-body'>
              <span className='task-label'>
                {taskLabel(task.id, suggestions, t)}
              </span>
              {task.id === SOMETHING_ELSE_TASK.id ? (
                <span className='task-eg'>
                  {t('wizard.tasks.somethingElseTagline')}
                </span>
              ) : (
                task.tagline && <span className='task-eg'>{task.tagline}</span>
              )}
            </span>
            <span className='task-check'>
              <svg
                viewBox='0 0 16 16'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.6'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
              >
                <path d='M3 8.5l3.2 3.2L13 5' />
              </svg>
            </span>
          </button>
        ))}
      </div>

      <div className={`validation${showValidation ? ' show' : ''}`}>
        <svg
          viewBox='0 0 16 16'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
        >
          <circle cx='8' cy='8' r='6.25' />
          <path d='M8 5v3.5M8 11h.01' />
        </svg>
        {t('wizard.tasks.validation')}
      </div>

      <div className='handoff-line'>
        <WhatsappColorIcon />
        <span>{t('wizard.tasks.handoff', { name: assistantName })}</span>
      </div>
    </section>
  );
};

export default TasksStep;
